import React from 'react';
import PropTypes from 'prop-types';
import useLockOverview from '../../hooks/useLockOverview';
import { getWeekOptionLabel } from '../../utils/lockOverviewUtils';
import { formatLockStatusLabel, getLockStatusClassName, isLiveLockStatus, shouldDisplayLockStatus } from '../../utils/lockStatusUtils';
import HiddenText from '../HiddenText';
import MobileLockCard from './MobileLockCard';
import styles from './MobileLockOverview.module.css';
import '../LockOverview/LockOverview.css';

const MobileLockOverview = ({ season, leaderboardData, navigationRequest, onNavigationHandled }) => {
  const overview = useLockOverview({
    season,
    leaderboardData,
    navigationRequest,
    onNavigationHandled,
  });
  const {
    viewMode,
    setViewMode,
    selectedWeek,
    setSelectedWeek,
    selectedUser,
    setSelectedUser,
    lockData,
    loading,
    error,
    availableWeeks,
    availableUsers,
    calendarData,
    currentWeek,
    locksForWeek,
    allWeeks,
    getLockForWeek,
    getWeekVisibility,
    getUserWeekVisibility,
    isUsernameClickable,
    handleUsernameClick,
    handleWeekClick,
    showStaleDataMessage,
    previousWeek,
    getSimpsonsName,
  } = overview;

  if (loading && !lockData) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading locks...</p>
      </div>
    );
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  const renderModeToggle = () => (
    <div className={styles.modeToggle}>
      <button
        type="button"
        className={`${styles.modeButton} ${viewMode === 'by-week' ? styles.modeActive : ''}`}
        onClick={() => setViewMode('by-week')}
      >
        By Week
      </button>
      <button
        type="button"
        className={`${styles.modeButton} ${viewMode === 'by-user' ? styles.modeActive : ''}`}
        onClick={() => setViewMode('by-user')}
      >
        By User
      </button>
    </div>
  );

  const renderStaleBanner = () => {
    if (!showStaleDataMessage) return null;
    return (
      <div className={styles.staleBanner}>
        {currentWeek === 1
          ? 'Week 1 data will be available once you complete your lock.'
          : `Lock overview reflects data through Week ${previousWeek}. Full data after you complete your lock.`}
      </div>
    );
  };

  const renderWeekSelector = () => (
    <div className={styles.selectorRow}>
      <label htmlFor="mobile-week-select">Week</label>
      <select
        id="mobile-week-select"
        value={selectedWeek}
        onChange={(e) => setSelectedWeek(parseInt(e.target.value, 10))}
      >
        {(availableWeeks.length > 0 ? availableWeeks : [selectedWeek]).map((week) => (
          <option key={week} value={week}>
            {getWeekOptionLabel(week, calendarData)}
          </option>
        ))}
      </select>
    </div>
  );

  const renderUserSelector = () => (
    <div className={styles.selectorRow}>
      <label htmlFor="mobile-user-select">User</label>
      <select
        id="mobile-user-select"
        value={selectedUser ?? ''}
        onChange={(e) => setSelectedUser(e.target.value)}
      >
        {availableUsers.map((username) => (
          <option key={username} value={username}>
            {username}
          </option>
        ))}
      </select>
    </div>
  );

  const renderWeekSummary = () => {
    if (!lockData?.summary) return null;
    return (
      <div className={styles.summaryStrip}>
        <div>
          <span className={styles.summaryLabel}>Record</span>
          <span className={styles.summaryValue}>{lockData.summary.leagueRecord}</span>
        </div>
        <div>
          <span className={styles.summaryLabel}>Avg margin</span>
          <span className={styles.summaryValue}>{lockData.summary.averageMargin}</span>
        </div>
      </div>
    );
  };

  const renderByWeek = () => (
    <>
      {renderWeekSelector()}
      {renderWeekSummary()}
      <div className={styles.cardList}>
        {locksForWeek.map((lock, index) => (
          <MobileLockCard
            key={`${lock.username}-${lock.lock}-${index}`}
            lock={lock}
            selectedWeek={selectedWeek}
            visibility={getWeekVisibility(lock)}
            isUsernameClickable={isUsernameClickable(lock)}
            onUsernameClick={handleUsernameClick}
            getSimpsonsName={getSimpsonsName}
          />
        ))}
      </div>
    </>
  );

  const renderUserProfile = () => {
    if (!lockData?.userProfile) return null;
    const profile = lockData.userProfile;
    return (
      <div className={styles.profileCard}>
        <div className={styles.profileGrid}>
          <div><span>Record</span><strong>{profile.record}</strong></div>
          <div><span>Rank</span><strong>{profile.ranking}</strong></div>
          <div><span>GB</span><strong>{profile.gamesBehind}</strong></div>
          <div><span>Avg margin</span><strong>{profile.averageMargin}</strong></div>
        </div>
      </div>
    );
  };

  const renderUserWeekRow = (weekInfo) => {
    const lock = getLockForWeek(weekInfo.week, weekInfo.pickNumber);
    const visibility = getUserWeekVisibility(weekInfo.week);
    const { shouldMaskRow, shouldBlurLockDetails, shouldBlurEverything } = visibility;
    const statusLabel = lock?.status ? formatLockStatusLabel(lock.status) : '-';
    const statusClass = getLockStatusClassName(lock?.status);
    const pulseClass = isLiveLockStatus(lock?.status) ? ' live-status-pulse' : '';

    const weekButton = (
      <button
        type="button"
        className={styles.weekLink}
        onClick={() => handleWeekClick(weekInfo.week)}
      >
        {weekInfo.week_display}
      </button>
    );

    return (
      <div key={`${weekInfo.week}-${weekInfo.pickNumber}`} className={styles.weekRow}>
        <div className={styles.weekRowHeader}>
          {weekButton}
          {!shouldMaskRow && !shouldBlurEverything && shouldDisplayLockStatus(lock) && (
            <span className={`${statusClass}${pulseClass}`}>{statusLabel}</span>
          )}
        </div>
        {shouldMaskRow ? (
          <HiddenText
            text="Hidden lock"
            tooltip="Nice try! Lock details hidden until you make your pick for this week."
            showTooltip
          />
        ) : shouldBlurEverything || shouldBlurLockDetails ? (
          <HiddenText
            text="Hidden pick"
            tooltip="Nice try! Lock details hidden until you make your pick."
            showTooltip
          />
        ) : lock ? (
          <div className={styles.weekRowBody}>
            <div className={styles.weekPick}>{lock.lock || '-'}</div>
            {lock.matchup && lock.lock !== 'Bye Week' && lock.lock !== 'No Pick' && (
              <div className={styles.weekMatchup}>{lock.matchup}</div>
            )}
            {lock.score && lock.score !== '-' && (
              <div className={styles.weekMeta}>Score {lock.score} • Margin {lock.margin ?? '-'}</div>
            )}
          </div>
        ) : (
          <div className={styles.weekRowBody}>
            <div className={styles.noPick}>No Pick</div>
          </div>
        )}
      </div>
    );
  };

  const renderByUser = () => (
    <>
      {renderUserSelector()}
      {renderUserProfile()}
      <div className={styles.timeline}>
        {allWeeks.map((weekInfo) => renderUserWeekRow(weekInfo))}
      </div>
    </>
  );

  return (
    <div className={styles.container}>
      {renderModeToggle()}
      {renderStaleBanner()}
      {viewMode === 'by-week' ? renderByWeek() : renderByUser()}
    </div>
  );
};

MobileLockOverview.propTypes = {
  season: PropTypes.number.isRequired,
  leaderboardData: PropTypes.object,
  navigationRequest: PropTypes.shape({
    viewMode: PropTypes.oneOf(['by-week', 'by-user']),
    username: PropTypes.string,
    week: PropTypes.number,
  }),
  onNavigationHandled: PropTypes.func,
};

export default MobileLockOverview;
